import axios from 'axios';
import { Message } from '../types';
import { getAuth } from 'firebase/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor to add auth token
apiClient.interceptors.request.use(async (config) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper to get ephemeral session ID (for guests)
export const getLocalSessionId = (): string => {
  let sessionId = localStorage.getItem('chat_session_id');
  if (!sessionId || sessionId.startsWith('fallback-')) {
    // Generate simpler ID
    sessionId = `session-${Date.now()}`;
    localStorage.setItem('chat_session_id', sessionId);
  }
  return sessionId;
};

// --- API Methods ---

export const getChats = async () => {
  const response = await apiClient.get('/chats');
  return response.data.chats;
};

export const createChat = async () => {
  const response = await apiClient.post('/chats', {});
  return response.data;
};

export const deleteChat = async (chatId: string) => {
  const response = await apiClient.delete(`/chats/${chatId}`);
  return response.data;
};

export const renameChat = async (chatId: string, title: string) => {
  const response = await apiClient.patch(`/chats/${chatId}`, { title });
  return response.data as { id: string; title: string };
};

export const sendMessage = async (
  messages: Message[],
  sessionId?: string // Now optional, if specific chat is targeted
): Promise<{ message: Message; sessionId: string }> => {
  try {
    // If no sessionId provided, use local guest ID
    const currentSessionId = sessionId || getLocalSessionId();

    const response = await apiClient.post('/chat', {
      messages: messages.map(m => ({ role: m.role, content: m.content })),
      sessionId: currentSessionId,
    });

    return {
      message: response.data.message,
      sessionId: response.data.sessionId || currentSessionId,
    };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const getChatHistory = async (sessionId: string) => {
  const response = await apiClient.get(`/chat/history/${sessionId}`);
  return response.data.history;
};

export const clearChatHistory = async (sessionId: string) => {
  const response = await apiClient.delete(`/chat/history/${sessionId}`);
  return response.data;
};